import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, MapPin, Eye, EyeOff, Trash2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { shareBoxToFacebook } from '@/utils/facebookShare';

const VendorBoxList = () => {
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, active, inactive
  const navigate = useNavigate();

  useEffect(() => {
    fetchBoxes();
  }, []);

  const fetchBoxes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendor/boxes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBoxes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      toast.error('Failed to load your boxes');
      setBoxes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (boxId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'reactivate';
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(
        `${API}/vendor/boxes/${boxId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Box ${action}d successfully`);
      fetchBoxes();
    } catch (error) {
      console.error(`Error ${action}ing box:`, error);
      toast.error(`Failed to ${action} box`);
    }
  };

  const handleRequestRemoval = async (boxId, boxName) => {
    if (!window.confirm(`Request permanent removal of "${boxName}"? This will create a support ticket for admin review.`)) {
      return;
    }
    
    const token = localStorage.getItem('token');
    
    try {
      await axios.post(
        `${API}/vendor/boxes/${boxId}/request-removal`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { reason: 'Vendor requested permanent removal' }
        }
      );
      
      toast.success('Removal request submitted. Admin will review your request.');
      // Deactivate box immediately
      await handleToggleActive(boxId, true);
    } catch (error) {
      console.error('Error requesting removal:', error);
      toast.error('Failed to submit removal request');
    }
  };

  const filteredBoxes = Array.isArray(boxes) ? boxes.filter(box => {
    if (filter === 'active') return box.is_active;
    if (filter === 'inactive') return !box.is_active;
    return true;
  }) : [];

  const categories = [
    { value: 'fruits', label: 'Fruits', color: 'bg-red-100 text-red-700' },
    { value: 'vegetables', label: 'Vegetables', color: 'bg-green-100 text-green-700' },
    { value: 'flowers', label: 'Flowers', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'eggs', label: 'Eggs', color: 'bg-orange-100 text-orange-700' },
    { value: 'jams', label: 'Jams', color: 'bg-purple-100 text-purple-700' },
    { value: 'baked_goods', label: 'Baked Goods', color: 'bg-amber-100 text-amber-700' },
    { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
  ];

  const getCategoryStyle = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.color || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-pulse">
          <MapPin className="w-16 h-16 text-green-500 mx-auto mb-4" />
        </div>
        <p className="text-gray-600">Loading your boxes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800" data-testid="my-boxes-title">My Honesty Boxes</h2>
          <p className="text-gray-600 mt-1">Manage your listings</p>
        </div>
        <Button
          onClick={() => navigate('/vendor/dashboard/boxes/new')}
          className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          data-testid="create-box-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Box
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-green-600' : ''}
          data-testid="filter-all"
        >
          All ({boxes.length})
        </Button>
        <Button
          variant={filter === 'active' ? 'default' : 'outline'}
          onClick={() => setFilter('active')}
          className={filter === 'active' ? 'bg-green-600' : ''}
          data-testid="filter-active"
        >
          Active ({boxes.filter(b => b.is_active).length})
        </Button>
        <Button
          variant={filter === 'inactive' ? 'default' : 'outline'}
          onClick={() => setFilter('inactive')}
          className={filter === 'inactive' ? 'bg-green-600' : ''}
          data-testid="filter-inactive"
        >
          Inactive ({boxes.filter(b => !b.is_active).length})
        </Button>
      </div>

      {/* Boxes Grid */}
      {filteredBoxes.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4" data-testid="no-boxes-message">
              {filter === 'all' ? 'No honesty boxes yet. Create your first one!' : `No ${filter} boxes`}
            </p>
            {filter === 'all' && (
              <Button
                onClick={() => navigate('/vendor/dashboard/boxes/new')}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Box
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredBoxes.map((box) => (
            <Card key={box.id} className="hover:shadow-xl transition-all duration-300" data-testid={`box-card-${box.id}`}>
              <CardHeader>
                {box.images && box.images.length > 0 && (
                  <img
                    src={box.images[0]}
                    alt={box.name}
                    className="w-full h-48 object-cover rounded-lg mb-3"
                    data-testid={`box-image-${box.id}`}
                  />
                )}
                <div className="flex items-start justify-between">
                  <CardTitle className="flex-1" data-testid={`box-name-${box.id}`}>{box.name}</CardTitle>
                  <Badge className={getCategoryStyle(box.category)}>
                    {categories.find(c => c.value === box.category)?.label}
                  </Badge>
                </div>
                <CardDescription className="flex items-center gap-2">
                  {box.is_active ? (
                    <Badge className="bg-green-100 text-green-700" data-testid={`box-status-${box.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> Active
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600" data-testid={`box-status-${box.id}`}>
                      <EyeOff className="w-3 h-3 mr-1" /> Inactive
                    </Badge>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-3" data-testid={`box-description-${box.id}`}>{box.description}</p>
                <div className="flex items-start gap-2 text-sm text-gray-500 mb-4">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span data-testid={`box-address-${box.id}`}>{box.address}</span>
                </div>
                
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => navigate(`/vendor/dashboard/boxes/${box.id}/edit`)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-green-500 text-green-700 hover:bg-green-50"
                      data-testid={`edit-box-btn-${box.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleToggleActive(box.id, box.is_active)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-blue-500 text-blue-700 hover:bg-blue-50"
                      data-testid={`toggle-active-btn-${box.id}`}
                    >
                      {box.is_active ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                      {box.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        shareBoxToFacebook(box, 'bump');
                        toast.success('Opening Facebook to share your honesty box!');
                      }}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-blue-600 text-blue-700 hover:bg-blue-50"
                      data-testid={`share-box-btn-${box.id}`}
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Share to Group
                    </Button>
                  </div>
                  <Button
                    onClick={() => handleRequestRemoval(box.id, box.name)}
                    variant="outline"
                    size="sm"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    data-testid={`request-removal-btn-${box.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Request Removal
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default VendorBoxList;